import { useEffect, useRef } from 'react';

/** Keep keyboard navigation in an open dialog and restore its trigger on close. */
export function useModalFocus(onClose: () => void) {
	const ref = useRef<HTMLDivElement>(null);
	const closeRef = useRef(onClose);
	const triggerRef = useRef<HTMLElement | null>(null);
	const restoreFrameRef = useRef<number | null>(null);
	closeRef.current = onClose;
	useEffect(() => {
		const panel = ref.current;
		if (!panel) return;
		if (restoreFrameRef.current !== null) {
			cancelAnimationFrame(restoreFrameRef.current);
			restoreFrameRef.current = null;
		}
		const active = document.activeElement;
		if (active instanceof HTMLElement && active !== document.body && !panel.contains(active)) {
			triggerRef.current = active;
		}
		const trigger = triggerRef.current;
		const triggerId = trigger?.id;
		const selector =
			'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]';
		const focusable = () =>
			Array.from(panel.querySelectorAll<HTMLElement>(selector)).filter(
				(item) => item.getClientRects().length > 0,
			);
		const focusFirst = () => (focusable()[0] ?? panel).focus();
		focusFirst();
		const containFocus = () => {
			if (!panel.contains(document.activeElement)) focusFirst();
		};
		// A focused retry control can disappear after an asynchronous response.
		const observer = new MutationObserver(containFocus);
		observer.observe(panel, { childList: true, subtree: true });
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				closeRef.current();
			} else if (event.key === 'Tab') {
				const items = focusable();
				const first = items[0];
				const last = items[items.length - 1];
				if (!first) {
					event.preventDefault();
					panel.focus();
				} else if (!panel.contains(document.activeElement)) {
					event.preventDefault();
					(event.shiftKey ? last : first)?.focus();
				} else if (
					event.shiftKey &&
					(document.activeElement === first || document.activeElement === panel)
				) {
					event.preventDefault();
					last?.focus();
				} else if (!event.shiftKey && document.activeElement === last) {
					event.preventDefault();
					first.focus();
				}
			}
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('focusin', containFocus);
		return () => {
			observer.disconnect();
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('focusin', containFocus);
			restoreFrameRef.current = requestAnimationFrame(() => {
				restoreFrameRef.current = null;
				const target = trigger?.isConnected
					? trigger
					: triggerId
						? document.getElementById(triggerId)
						: null;
				target?.focus();
			});
		};
	}, []);
	return ref;
}
